import React, { useContext, useEffect, useMemo, useState } from 'react'
import ReactModal from 'react-modal'
import { useTranslation } from 'react-i18next'
import ArchiveMedia from './ArchiveMedia'
import { ArchiveViewerContext } from './ArchiveBodies'
import { KindIcon } from './archiveKinds'
import { formatDay, isDayAnswered, latestJournalDay } from './JournalBody'

// Above the student feedback modal (z-index 100), which can host this one.
const MODAL_STYLE = {
    overlay: { zIndex: 110, backgroundColor: 'rgba(0, 0, 0, 0.3)', backdropFilter: 'blur(5px)' },
    // Full-viewport, invisible content box; the visible card is .archive-modal inside it.
    content: {
        inset: 0,
        padding: '40px 16px',
        background: 'transparent',
        border: 'none',
        borderRadius: 0,
        boxShadow: 'none',
        overflow: 'auto',
    },
}

const ORDINALS = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th']

const hasAnswer = (answer) => (Array.isArray(answer) ? answer.some((a) => `${a}`.trim()) : answer !== undefined && answer !== null && `${answer}`.trim() !== '')

function SurveyQuestionResult({ question, number }) {
    const { t } = useTranslation()
    const answered = hasAnswer(question.answer)
    const answer = Array.isArray(question.answer) ? question.answer.join(', ') : question.answer

    let options = null
    if (question.type === 'options') options = question.options || []
    if (question.type === 'yesNo') options = [t('Yes'), t('No')]
    // A yes/no answer is stored in the language it was given in; keep it visible if it differs now.
    if (options && answered && !options.includes(answer)) options = [...options, answer]

    return (
        <div className="archive-modal-question">
            <p className="archive-modal-question-title">
                {number}. {question.title}
            </p>
            {options ? (
                <div className={`archive-modal-options${options.length === 2 ? ' is-pair' : ''}`}>
                    {options.map((option, index) => {
                        const selected = answered && option === answer
                        return (
                            <span key={`${option}-${index}`} className={`archive-modal-option${selected ? ' is-selected' : ''}`} aria-current={selected || undefined}>
                                {option}
                            </span>
                        )
                    })}
                </div>
            ) : (
                answered && <p className="archive-modal-answer">{`${answer}`}</p>
            )}
            {!answered && <p className="archive-modal-muted">{t('archiveNotAnswered')}</p>}
        </div>
    )
}

function SurveyResult({ item }) {
    const { t } = useTranslation()
    const sections = item.response?.sections || []
    let number = 0
    return (
        <>
            <header className="archive-modal-header">
                <span className="archive-modal-kind">
                    <KindIcon kind="survey" size={26} />
                    {t('survey')}
                </span>
                <h2 className="archive-modal-title">{item.content?.title}</h2>
                {!!item.content?.description && <p className="archive-modal-description">{item.content.description}</p>}
                <p className="archive-modal-meta">
                    {t('archiveCompletedOn')} {formatDay(item.created_date)}
                </p>
            </header>
            <div className="archive-modal-body">
                {sections.map((section, sectionIndex) => (
                    <section key={sectionIndex} className="archive-modal-section">
                        {!!section?.title && <h3 className="archive-modal-section-title">{section.title}</h3>}
                        {!!section?.description && <p className="archive-modal-description">{section.description}</p>}
                        {(section?.questions || []).map((question, index) => {
                            if (!question) return null
                            number += 1
                            return <SurveyQuestionResult key={index} question={question} number={number} />
                        })}
                    </section>
                ))}
            </div>
        </>
    )
}

function JournalResult({ item, openImageBig }) {
    const { t } = useTranslation()
    const viewer = useContext(ArchiveViewerContext)
    const answersHidden = viewer === 'staff'
    const days = useMemo(() => item.response?.days || [], [item])
    const [selected, setSelected] = useState(() => latestJournalDay(days)?.number ?? 0)
    const day = days.find((entry) => entry.number === selected) || days[0]
    const name = item.content?.name || item.content?.title

    useEffect(() => {
        document.getElementById(`archive-modal-day-${selected}`)?.scrollIntoView({ block: 'nearest', inline: 'center' })
    }, [selected])

    return (
        <>
            <header className="archive-modal-header archive-modal-header--journal">
                <span className="archive-modal-kind">
                    <KindIcon kind="journal" size={26} />
                    {t('archiveKindJournal')}
                </span>
                <h2 className="archive-modal-title">{name}</h2>
                {!!item.content?.description && <p className="archive-modal-description">{item.content.description}</p>}
                <p className="archive-modal-privacy">{answersHidden ? t('archiveJournalStaffPrivacy') : t('archiveJournalPrivacy')}</p>
            </header>
            <div className="archive-modal-body">
                <div className="archive-modal-days" role="tablist" aria-label={name}>
                    {days.map((entry) => {
                        const isSelected = entry.number === selected
                        return (
                            <button
                                type="button"
                                role="tab"
                                aria-selected={isSelected}
                                id={`archive-modal-day-${entry.number}`}
                                key={entry.number}
                                className={`archive-modal-day${isDayAnswered(entry) ? ' is-answered' : ''}${isSelected ? ' is-selected' : ''}`}
                                onClick={() => setSelected(entry.number)}
                            >
                                {t('day')} {entry.number + 1}
                            </button>
                        )
                    })}
                </div>
                {!!day && (
                    <div className="archive-modal-day-panel" role="tabpanel">
                        {!!day.files?.storage_name && <ArchiveMedia files={day.files} title={day.title} openImageBig={openImageBig} />}
                        {!!day.title && <h3 className="archive-modal-section-title">{day.title}</h3>}
                        {!!day.description && <p className="archive-modal-description">{day.description}</p>}
                        <div className="archive-modal-question">
                            <p className="archive-modal-label">{t('question')}:</p>
                            <p className="archive-modal-question-title">{day.question}</p>
                            {!isDayAnswered(day) ? (
                                <p className="archive-modal-muted">{t('archiveNotAnswered')}</p>
                            ) : answersHidden ? (
                                <p className="archive-modal-private">{t('archiveJournalPrivateNote')}</p>
                            ) : (
                                day.answers.map((answer, index) => (
                                    <div className="archive-modal-ordinal" key={index}>
                                        <span>{ORDINALS[index] || index + 1}</span>
                                        <p className="archive-modal-answer">{answer}</p>
                                    </div>
                                ))
                            )}
                            {!!day.answered_at && (
                                <p className="archive-modal-meta archive-modal-meta--end">
                                    {t('archiveJournalWrittenOn')} {formatDay(day.answered_at)}
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    )
}

/**
 * Read-only view of a completed survey or journal. Nothing in it can be edited or
 * resubmitted; for staff, journal answers are never shown (the API withholds them).
 */
export default function ArchiveResultModal({ item, onClose, openImageBig }) {
    const { t } = useTranslation()
    return (
        <ReactModal isOpen={!!item} ariaHideApp={false} onRequestClose={onClose} style={MODAL_STYLE}>
            {!!item && (
                <div className="archive-modal" role="document">
                    <button type="button" className="archive-modal-close" onClick={onClose} aria-label={t('close')}>
                        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
                            <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </button>
                    {item.kind === 'survey' && <SurveyResult item={item} />}
                    {item.kind === 'journal' && <JournalResult item={item} openImageBig={openImageBig} />}
                </div>
            )}
        </ReactModal>
    )
}
